/**
 * Pre-order product page: modal for delivery region + date, then add to cart as line properties.
 */
(function () {
  const R = window.BellaDeliveryRules || {};
  const startOfDay =
    R.startOfDay ||
    function (d) {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };
  const isoDate =
    R.isoDate ||
    function (d) {
      const x = new Date(d);
      return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    };
  const getMinSelectableDate = function (region) {
    if (R.getPdpMinSelectableDate) return R.getPdpMinSelectableDate(region);
    if (R.getMinSelectableDate) return R.getMinSelectableDate(region, 'pdp');
    return startOfDay(new Date());
  };
  const isBeforeMinSelectable = function (date, region) {
    if (R.isBeforeMinSelectable) return R.isBeforeMinSelectable(date, region, 'pdp');
    return startOfDay(date).getTime() < getMinSelectableDate(region).getTime();
  };
  const monthLabel =
    R.monthLabel ||
    function (d) {
      return d.toLocaleDateString(document.documentElement.lang || undefined, {
        month: 'long',
        year: 'numeric',
      });
    };

  function formatAdminDate(d) {
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function strings() {
    const v = window.theme?.variantStrings || {};
    return {
      selectDateError: v.preorderSelectDateError || 'Please select a delivery date.',
    };
  }

  function syncDynamicCheckout(form, enabled) {
    const shopifyPayment = form?.querySelector('.shopify-payment-button');
    if (!shopifyPayment) return;
    shopifyPayment.style.pointerEvents = enabled ? '' : 'none';
    shopifyPayment.style.opacity = enabled ? '' : '0.45';
    shopifyPayment.toggleAttribute('inert', !enabled);
  }

  class PreorderProductModal {
    constructor(productFormEl) {
      this.productFormEl = productFormEl;
      this.form = productFormEl.querySelector('form[data-type="add-to-cart-form"]');
      this.modal = productFormEl.querySelector('[data-preorder-modal]');
      if (!this.form || !this.modal) return;

      this.sheet = this.modal.querySelector('[data-preorder-sheet]');
      this.grid = this.modal.querySelector('[data-preorder-cal-grid]');
      this.monthEl = this.modal.querySelector('[data-preorder-cal-month]');
      this.deliveryToInput = productFormEl.querySelector('[data-preorder-delivery-to-input]');
      this.dateInput = productFormEl.querySelector('[data-preorder-date-input]');
      this.submitBtn = this.form.querySelector('[data-preorder-submit]');

      this.region = 'dubai';
      this.calView = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      this.tempSelected = null;
      this._submitting = false;
      this.modalPlaceholder = document.createComment('preorder-modal');

      if (this.modal.parentNode) {
        this.modal.parentNode.insertBefore(this.modalPlaceholder, this.modal);
      }

      this.bindEvents();
      syncDynamicCheckout(this.form, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'product-form') this.clearLineProperties();
        });
      }
    }

    clearLineProperties() {
      if (this.deliveryToInput) this.deliveryToInput.value = '';
      if (this.dateInput) this.dateInput.value = '';
      delete this.productFormEl.dataset.preorderDeliveryTo;
      delete this.productFormEl.dataset.preorderDeliveryDate;
    }

    bindEvents() {
      this.submitBtn?.addEventListener('click', (e) => {
        if (this._submitting) return;
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
      });

      this.modal.querySelectorAll('[data-preorder-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeModal();
        });
      });

      this.modal.querySelectorAll('[data-preorder-region]').forEach((btn) => {
        btn.addEventListener('click', () => this.setRegion(btn.getAttribute('data-preorder-region')));
      });

      this.modal.querySelector('[data-preorder-cal-prev]')?.addEventListener('click', () => this.stepMonth(-1));
      this.modal.querySelector('[data-preorder-cal-next]')?.addEventListener('click', () => this.stepMonth(1));

      this.modal.querySelector('[data-preorder-confirm]')?.addEventListener('click', () => this.confirmAndAdd());

      this.grid?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-preorder-cal-day]');
        if (btn && !btn.disabled) {
          const y = parseInt(btn.getAttribute('data-cal-year'), 10);
          const m = parseInt(btn.getAttribute('data-cal-month'), 10);
          const d = parseInt(btn.getAttribute('data-preorder-cal-day'), 10);
          this.tempSelected = new Date(y, m, d);
          this.renderCalendar();
        }
      });

      this._onKey = (e) => {
        if (e.code === 'Escape') this.closeModal();
      };

      this.form.addEventListener(
        'submit',
        (e) => {
          if (this.submitBtn?.dataset.preorderSubmit === undefined) return;
          if (this._submitting) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.openModal();
        },
        true
      );
    }

    setRegion(region) {
      this.region = region === 'other' ? 'other' : 'dubai';
      const segment = this.modal.querySelector('[data-preorder-region-segment]');
      segment?.classList.toggle('is-region-other', this.region === 'other');
      this.modal.querySelectorAll('[data-preorder-region]').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.getAttribute('data-preorder-region') === this.region);
      });
      this.modal.querySelectorAll('[data-preorder-info]').forEach((box) => {
        const key = box.getAttribute('data-preorder-info');
        box.hidden = key !== this.region;
      });
      if (this.tempSelected && isBeforeMinSelectable(this.tempSelected, this.region)) {
        this.tempSelected = null;
      }
      this.renderCalendar();
    }

    portalModalToBody() {
      if (this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal);
      }
    }

    restoreModalFromBody() {
      if (this.modal.parentNode === document.body && this.modalPlaceholder.parentNode) {
        this.modalPlaceholder.parentNode.insertBefore(this.modal, this.modalPlaceholder.nextSibling);
      }
    }

    openModal() {
      this.clearLineProperties();
      this.tempSelected = null;
      this.setRegion('dubai');
      this.portalModalToBody();
      this.modal.hidden = false;
      this.sheet?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('preorder-date-sheet-open');
      document.addEventListener('keydown', this._onKey);
      requestAnimationFrame(() => {
        this.sheet?.classList.add('is-open');
      });
    }

    closeModal() {
      this.sheet?.classList.remove('is-open');
      this.sheet?.setAttribute('aria-hidden', 'true');
      this.modal.hidden = true;
      document.body.classList.remove('preorder-date-sheet-open');
      document.removeEventListener('keydown', this._onKey);
      this.restoreModalFromBody();
    }

    setLineProperties(deliveryToLabel, dateLabel) {
      if (this.deliveryToInput) this.deliveryToInput.value = deliveryToLabel;
      if (this.dateInput) this.dateInput.value = dateLabel;
      this.productFormEl.dataset.preorderDeliveryTo = deliveryToLabel;
      this.productFormEl.dataset.preorderDeliveryDate = dateLabel;
    }

    confirmAndAdd() {
      const s = strings();
      if (!this.tempSelected || isBeforeMinSelectable(this.tempSelected, this.region)) {
        this.showError(s.selectDateError);
        return;
      }

      const deliveryToLabel = this.region === 'other' ? 'Other Emirates' : 'Dubai';
      const dateLabel = formatAdminDate(this.tempSelected);
      this.setLineProperties(deliveryToLabel, dateLabel);

      this._submitting = true;
      this.closeModal();

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(this.submitBtn || undefined);
      } else {
        this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }

      window.setTimeout(() => {
        this._submitting = false;
        this.clearLineProperties();
      }, 500);
    }

    showError(msg) {
      const productForm = this.productFormEl;
      if (productForm?.handleErrorMessage) {
        productForm.handleErrorMessage(msg);
      } else {
        alert(msg);
      }
    }

    stepMonth(dir) {
      this.calView.setMonth(this.calView.getMonth() + dir);
      this.renderCalendar();
    }

    renderCalendar() {
      if (!this.grid || !this.monthEl) return;
      const view = this.calView;
      this.monthEl.textContent = monthLabel(view);

      const y = view.getFullYear();
      const m = view.getMonth();
      const firstDow = new Date(y, m, 1).getDay();
      const daysInMonth = new Date(y, m + 1, 0).getDate();
      const today = startOfDay(new Date());
      const minSelectable = getMinSelectableDate(this.region);

      let html = '';
      for (let i = 0; i < firstDow; i++) {
        html += '<span class="bella-cal__pad" aria-hidden="true"></span>';
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const cell = startOfDay(new Date(y, m, d));
        const isPast = cell.getTime() < minSelectable.getTime();
        const isToday = cell.getTime() === today.getTime();
        const isSel = this.tempSelected && startOfDay(this.tempSelected).getTime() === cell.getTime();

        let cls = 'bella-cal__day';
        if (isToday) cls += ' is-today';
        if (isPast) cls += ' is-muted';
        if (isSel) cls += ' is-selected';

        html +=
          '<button type="button" class="' +
          cls +
          '" data-preorder-cal-day="' +
          d +
          '" data-cal-year="' +
          y +
          '" data-cal-month="' +
          m +
          '"' +
          (isPast ? ' disabled' : '') +
          '>' +
          d +
          '</button>';
      }
      this.grid.innerHTML = html;

      const confirmBtn = this.modal.querySelector('[data-preorder-confirm]');
      if (confirmBtn) {
        confirmBtn.disabled = !this.tempSelected || isBeforeMinSelectable(this.tempSelected, this.region);
      }
    }
  }

  function init() {
    document.querySelectorAll('product-form').forEach((pf) => {
      if (!pf.querySelector('[data-preorder-submit]') || pf.dataset.preorderModalInit) return;
      pf.dataset.preorderModalInit = '1';
      new PreorderProductModal(pf);
    });
  }

  document.addEventListener('productInfo:loaded', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
